import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../common/api';
import authService from '../auth/auth-service';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import Alerts from '../alert/Alerts';
import { useAlerts } from '../alert/AlertContext';

const passwordMeetsPolicy = (value: string): boolean => {
  if (value.length < 8) {
    return false;
  }
  if (!/[a-zA-Z]/.test(value)) {
    return false;
  }
  if (!/[0-9]/.test(value)) {
    return false;
  }
  return true;
};

const PasswordResetPage: React.FC = () => {
  const alerts = useAlerts();
  const [searchParams] = useSearchParams();
  const emailAddress = searchParams.get('emailAddress') ?? '';
  const code = searchParams.get('code') ?? '';
  const paramsValid = emailAddress !== '' && code !== '';

  const logoutOnceRef = useRef(false);
  const [newPassword, setNewPassword] = useState('');
  const [verifyNewPassword, setVerifyNewPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useDocumentTitle('Reset Password');

  useEffect(() => {
    if (!paramsValid) {
      if (!logoutOnceRef.current) {
        logoutOnceRef.current = true;
        authService.logout();
      }
    }
  }, [paramsValid]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setSubmitted(true);
      if (!paramsValid) {
        return;
      }
      if (!passwordMeetsPolicy(newPassword) || verifyNewPassword !== newPassword) {
        return;
      }

      setSubmitting(true);
      api
        .put('/staff-user/password/reset', {
          emailAddress,
          code,
          newPassword,
        })
        .then((res) => {
          setSubmitting(false);
          if (res.data?.success) {
            setNewPassword('');
            setVerifyNewPassword('');
            setSubmitted(false);
            authService.logout();
            alerts.success('Your password was successfully changed');
          } else {
            alerts.error('An error occurred while resetting your password');
          }
        })
        .catch(() => {
          setSubmitting(false);
          alerts.error('An error occurred while resetting your password');
        });
    },
    [paramsValid, emailAddress, code, newPassword, verifyNewPassword]
  );

  if (!paramsValid) {
    return null;
  }

  const showPasswordRequired = submitted && !newPassword;
  const showPasswordPolicyError =
    submitted && newPassword !== '' && !passwordMeetsPolicy(newPassword);
  const showMismatchError = submitted && verifyNewPassword !== '' && verifyNewPassword !== newPassword;
  const showVerifyRequired = submitted && !verifyNewPassword;

  return (
    <div id="main" role="main">
      <form className="lockscreen animated flipInY" onSubmit={handleSubmit} noValidate>
        <div className="logo">
          <h1 className="semi-bold">
            <img src="img/logo-o.png" alt="" /> Relcore
          </h1>
        </div>
        <div>
          <h1>
            <small>Reset Password</small>
          </h1>

          <Alerts />

          <div className="form-group">
            <input type="text" name="emailAddress" className="form-control" value={emailAddress} disabled readOnly />
          </div>

          <div
            className={`form-group${showPasswordRequired || showPasswordPolicyError ? ' has-error' : ''} mt-1`}>
            <input
              type="password"
              name="password"
              placeholder="Password"
              className="form-control"
              value={newPassword}
              onChange={(ev) => setNewPassword(ev.target.value)}
              autoComplete="new-password"
            />
            {showPasswordRequired && (
              <span className="help-block text-danger">This is required</span>
            )}
            {showPasswordPolicyError && (
              <span className="help-block text-danger">8 characters minimum and at least 1 number</span>
            )}
          </div>

          <div className={`form-group${showMismatchError || showVerifyRequired ? ' has-error' : ''} mt-1`}>
            <div className="input-group">
              <input
                type="password"
                name="verifyNewPassword"
                placeholder="Verify Password"
                className="form-control"
                value={verifyNewPassword}
                onChange={(ev) => setVerifyNewPassword(ev.target.value)}
                autoComplete="new-password"
              />
              <div className="input-group-btn">
                <button
                  className="btn btn-primary"
                  type="submit"
                  disabled={submitting || !passwordMeetsPolicy(newPassword) || verifyNewPassword !== newPassword}>
                  Update
                </button>
              </div>
            </div>
            {showMismatchError && <span className="help-block text-danger">Passwords do not match</span>}
            {showVerifyRequired && !showMismatchError && (
              <span className="help-block text-danger">This is required</span>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default PasswordResetPage;
